// Idle / AFK detector + Emperor fining.
//
//   ACTIVE  ──60s idle──▶  WARNING  ──30s more idle──▶  FINED
//      ▲                       │                          │ ──60s──▶ recurring fine
//      └──────── any activity ─┴──────────────────────────┘  (loops while FINED)
//
// While FINED:
//   • One-time initial fine: −5% of treasury, taken by the kingdom's Emperor
//   • Recurring fine every 60s: −1% of treasury
//   • Goon click rate drops to 25%
//   • Tax tribute is intercepted (0× to player)
//
// Activity = mousemove / click / keydown / touchstart. On any activity the
// player returns to ACTIVE and all multipliers snap back to normal.

import { Events, emit } from '../ui/eventBus.js'
import { IdleConfig, KingdomEmperors } from '../config/balance.js'
import * as season from './seasonManager.js'
import { activeId } from './state.js'
import { isAdventuring } from './adventureManager.js'
import { isOnTrip } from './capitalTrip.js'

const STATE = { ACTIVE: 'active', WARNING: 'warning', FINED: 'fined' }

let state = STATE.ACTIVE
let lastActivityAt = 0
let fineAccumMs = 0 // time accumulated since the last recurring fine
let finedAt = 0     // when FINED state started (for raid-after-fine timer)
let raidActive = false
let paused = false  // player-initiated pause — freezes idle fines AND income

export function init() {
  bumpActivity()
  ;['mousemove', 'click', 'keydown', 'touchstart'].forEach((ev) => {
    document.addEventListener(ev, bumpActivity, { passive: true })
  })
  setInterval(tick, 1000)
}

export function isFined() {
  return state === STATE.FINED
}

// ── Player pause (stickiness): freeze the game while you're away ─────────────
// While paused, the Emperor's idle penalty is suspended (no warning / fine /
// raid) AND tribute income stops (taxManager.getRate returns 0) — a fair "step
// away" that can't be exploited for free idle coin. In-memory only: reloading
// resumes play (a reload is itself fresh engagement).
export function isPaused() {
  return paused
}
export function setPaused(v) {
  v = !!v
  if (v === paused) return
  paused = v
  // Snap out of any warning/fine state and clear the overlay either way.
  state = STATE.ACTIVE
  fineAccumMs = 0
  finedAt = 0
  if (raidActive) { raidActive = false; emit(Events.RaidEnd, {}) }
  lastActivityAt = Date.now()
  season.setLastTaxTick(Date.now()) // don't credit the paused window on resume
  emit(Events.IdleResume, {})       // hide any idle overlay
  emit(Events.PauseChange, { paused })
}
export function togglePause() {
  setPaused(!paused)
}

export function getState() {
  return state
}

// Single source of truth for the goon click-rate multiplier given the
// current idle state. goonManager calls this each tick instead of branching
// on isFined() — that way ACTIVE / WARNING / FINED all stay in sync with
// IdleConfig in one place.
export function getGoonRateMult() {
  if (state === STATE.FINED) return IdleConfig.finedGoonRateMult
  if (state === STATE.WARNING) return IdleConfig.warningGoonRateMult
  return IdleConfig.activeGoonRateMult
}

export function msSinceActivity() {
  return Date.now() - lastActivityAt
}

export function msUntilFine() {
  return Math.max(0, IdleConfig.fineAfterMs - msSinceActivity())
}

function getEmperor() {
  return KingdomEmperors[activeId()] ?? KingdomEmperors.WEI
}

function bumpActivity() {
  const wasNonActive = state !== STATE.ACTIVE
  lastActivityAt = Date.now()
  if (wasNonActive) {
    state = STATE.ACTIVE
    fineAccumMs = 0
    finedAt = 0
    // Freeze the tax clock so retroactive offline-catchup doesn't credit the
    // AFK window once the loops resume normally.
    season.setLastTaxTick(Date.now())
    emit(Events.IdleResume, {})
  }
  // If a raid was active, end it the moment the player engages — the
  // existing wild goons should scatter for the edges (handled in wildGoons.js).
  if (raidActive) {
    raidActive = false
    emit(Events.RaidEnd, {})
  }
}

function tick() {
  // Player paused → the world is frozen: keep the activity clock fresh so we
  // resume cleanly, freeze the tax clock, and run no idle state machine.
  if (paused) {
    lastActivityAt = Date.now()
    season.setLastTaxTick(Date.now())
    return
  }

  // While the cat is on an adventure, the player is intentionally away from
  // the field — the Emperor's idle fines are paused. Home risk during an
  // adventure is modeled separately by the adventure's own raid profile.
  // Keep the activity clock fresh so the player returns to ACTIVE cleanly on
  // recall (no stale WARNING/FINE the instant they're back).
  // Same for a visit to the Capital: the player is intentionally away, and home risk is modelled
  // by the trip's own raid roll (capitalTrip.js) instead of idle fines.
  if (isAdventuring() || isOnTrip()) {
    lastActivityAt = Date.now()
    if (state !== STATE.ACTIVE) {
      state = STATE.ACTIVE
      fineAccumMs = 0
      finedAt = 0
      if (raidActive) { raidActive = false; emit(Events.RaidEnd, {}) }
      season.setLastTaxTick(Date.now())
      emit(Events.IdleResume, {})
    }
    return
  }

  const ms = msSinceActivity()

  if (state === STATE.ACTIVE && ms >= IdleConfig.warnAfterMs && ms < IdleConfig.fineAfterMs) {
    state = STATE.WARNING
    emit(Events.IdleWarn, { msUntilFine: msUntilFine(), emperor: getEmperor() })
    return
  }

  if (state === STATE.WARNING && ms < IdleConfig.fineAfterMs) {
    // Refresh countdown every second so the overlay's seconds-remaining ticks.
    emit(Events.IdleWarn, { msUntilFine: msUntilFine(), emperor: getEmperor() })
    return
  }

  if (state === STATE.WARNING && ms >= IdleConfig.fineAfterMs) {
    state = STATE.FINED
    fineAccumMs = 0
    finedAt = Date.now()
    const emp = getEmperor()
    const amount = Math.ceil(season.getWealth() * IdleConfig.initialFinePct)
    const taken = season.fineWealth(amount)
    season.setLastTaxTick(Date.now()) // freeze tax clock during fine state
    emit(Events.IdleFineInitial, { taken, emperor: emp })
    return
  }

  if (state === STATE.FINED) {
    fineAccumMs += 1000
    if (fineAccumMs >= IdleConfig.recurringIntervalMs) {
      fineAccumMs -= IdleConfig.recurringIntervalMs
      const emp = getEmperor()
      const amount = Math.ceil(season.getWealth() * IdleConfig.recurringFinePct)
      const taken = season.fineWealth(amount)
      season.setLastTaxTick(Date.now())
      emit(Events.IdleFineRecurring, { taken, emperor: emp })
    }
    // After the player has been FINED for raidAfterFineMs and still hasn't
    // engaged, the bandit tribes pour in. wildGoons.js boosts spawn rate +
    // alive cap while raidActive; the emperor's raid line goes to the beat
    // overlay so the player gets a clear "you broke the kingdom" cue.
    if (!raidActive && Date.now() - finedAt >= IdleConfig.raidAfterFineMs) {
      raidActive = true
      const emp = getEmperor()
      emit(Events.RaidStart, { emperor: emp })
      emit(Events.NarrativeBeat, {
        title: `${emp.name} ${emp.cn} — The Tribes Descend`,
        body: `${emp.raid ?? '"The bandits come because you would not lift a finger."'}\n\nWhat to do: walk your cat into the raiders to slay them (or press 1 to aim and shoot), keep your citizens near a Temple to convert them, and click to keep the Emperor from fining you more.`,
        kingdom: activeId(),
      })
    }
  }
}

export function isRaidActive() {
  return raidActive
}
